/**
 * GPACL Frontend Module Registry.
 *
 * Provides a single gform_is_value_match registration point.
 * Each module registers itself via registerModule() before
 * initModuleRegistry() is called.
 *
 * Evaluation order follows registration order (insertion order).
 * Advanced Logic must be registered first so its sentinel interception
 * runs before operator modules.
 *
 * @package
 */

/**
 * Interface for a GPACL frontend module.
 */
export interface FrontendModule {
	/** Unique slug for this module (used for debugging). */
	slug: string;

	/**
	 * Return true if this module should handle the given rule.
	 * Called for every rule evaluation — keep this fast.
	 *
	 * @param rule   The conditional logic rule.
	 * @param formId The form ID (needed to construct DOM selectors like #field_{formId}_{fieldId}).
	 */
	handles: (
		rule: { fieldId: string | number; operator: string; value: string },
		formId: number
	) => boolean;

	/**
	 * Evaluate the rule and return the match result.
	 * Only called when handles() returns true.
	 *
	 * @param isMatch Current match result.
	 * @param formId  The form ID.
	 * @param rule    The conditional logic rule.
	 */
	evaluate: (
		isMatch: boolean,
		formId: number,
		rule: { fieldId: string | number; operator: string; value: string }
	) => boolean;
}

const modules: FrontendModule[] = [];
let initialized = false;

/**
 * Register a module with the registry.
 *
 * Must be called before initModuleRegistry().
 *
 * @param mod The module to register.
 */
export function registerModule(mod: FrontendModule): void {
	if (initialized) {
		// eslint-disable-next-line no-console
		console.warn(
			`[GPACL] registerModule('${mod.slug}') called after initModuleRegistry(). Module will not be active.`
		);
		return;
	}
	modules.push(mod);
}

/**
 * Initialize the registry by registering a single gform_is_value_match filter.
 *
 * The filter iterates registered modules in order. The first module whose
 * handles() returns true gets to evaluate the rule. Subsequent modules are
 * skipped for that rule (first-match wins).
 *
 * Advanced Logic must be registered first so its sentinel interception
 * runs before operator modules.
 */
export function initModuleRegistry(): void {
	if (typeof gform === 'undefined' || !gform.addFilter) {
		return;
	}

	initialized = true;

	gform.addFilter(
		'gform_is_value_match',
		(
			isMatch: boolean,
			formId: number,
			rule: { fieldId: string | number; operator: string; value: string }
		): boolean => {
			for (const mod of modules) {
				if (mod.handles(rule, formId)) {
					return mod.evaluate(isMatch, formId, rule);
				}
			}
			return isMatch;
		}
	);
}
